import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldX } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuCard, NeuInput } from "../../components/neu/Neu";
import { deleteMyAccountNow, requestDeleteCode } from "../../api/account";
import { useAuth } from "../../context/AuthContext";
import { DeleteAccountCode } from "./DeleteAccountCode";

// Permanent deletion (Apple 5.1.1(v)) confirmed by a 6-digit code emailed to the user.
export const DeleteAccountCard = () => {
  const { t } = useTranslation();
  const { signOut, user } = useAuth();
  const [step, setStep] = useState("closed");
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const sendCode = async () => {
    setBusy(true);
    try {
      await requestDeleteCode();
      setStep("code");
      toast.success(t("account.deleteCodeSent"));
    } catch {
      toast.error(t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await deleteMyAccountNow(reason, code.replace(/\D/g, ""));
      toast.success(t("account.deleteDone"));
      setTimeout(() => signOut(), 1200);
    } catch (err) {
      toast.error(t(`archiveCompany.err.${err?.response?.data?.detail?.code || "x"}`, t("errors.generic")));
      setBusy(false);
    }
  };

  return (
    <NeuCard data-testid="delete-account-card">
      <div className="flex items-start justify-between gap-3">
        <p className="label-xs">{t("account.deleteTitle")}</p>
        <ShieldX className="h-5 w-5 text-destructive" />
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{t("account.deleteHintNow")}</p>
      <Link to="/delete-account" data-testid="delete-account-policy" className="mt-2 inline-block text-xs font-bold text-primary">
        {t("legal.deletion")}
      </Link>

      {step === "closed" ? (
        <NeuButton variant="danger" className="mt-4 w-full justify-center py-3 sm:w-auto sm:px-6"
          data-testid="delete-account-open" onClick={() => setStep("reason")}>
          {t("account.deleteAction")}
        </NeuButton>
      ) : (
        <div className="mt-4 grid gap-3">
          <NeuInput data-testid="delete-account-reason" placeholder={t("account.deleteReason")}
            value={reason} onChange={(e) => setReason(e.target.value)} />
          {step === "code" ? <DeleteAccountCode code={code} setCode={setCode} email={user?.email} /> : null}
          <div className="flex flex-col gap-2 sm:flex-row">
            {step === "reason" ? (
              <NeuButton variant="danger" className="w-full justify-center py-3 sm:w-auto sm:px-6"
                data-testid="delete-account-send-code" disabled={busy} onClick={sendCode}>
                {t("account.deleteSendCode")}
              </NeuButton>
            ) : (
              <NeuButton variant="danger" className="w-full justify-center py-3 sm:w-auto sm:px-6"
                data-testid="delete-account-confirm" disabled={busy || code.replace(/\D/g, "").length !== 6}
                onClick={submit}>
                {t("account.deleteConfirmNow")}
              </NeuButton>
            )}
            <NeuButton className="w-full justify-center py-3 sm:w-auto sm:px-6"
              data-testid="delete-account-cancel" onClick={() => { setStep("closed"); setCode(""); }}>
              {t("app.cancel")}
            </NeuButton>
          </div>
        </div>
      )}
    </NeuCard>
  );
};
